import { AlertCircleIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Card, CardContent } from '@/modules/ui/components/card/Card';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import {
  UserForm,
  type UserFormValues,
} from '@/modules/users/components/UserForm';
import { useUpdateUserMutation } from '@/modules/users/mutations/useUpdateUserMutation';
import { useUserQuery } from '@/modules/users/queries/useUserQuery';
import type { UserUpdateRequestApi } from '@/modules/users/types';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const UserEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const userQuery = useUserQuery(id);
  const updateUserMutation = useUpdateUserMutation();

  const handleSubmit = async (values: UserFormValues) => {
    if (!id) return;

    const apiValues: UserUpdateRequestApi = {
      firstName: values.firstName || null,
      lastName: values.lastName || null,
      meetingUrl: values.meetingUrl || null,
    };

    updateUserMutation.mutate(
      { id, ...apiValues },
      {
        onSuccess: async () => {
          toast.success('User has been updated');
          navigate('/users');
        },
        onError: (error) => {
          toast.error(
            `User has not been updated: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <Container>
      <PageHeader title="Edit User" />

      {userQuery.isLoading && <Loader />}
      {userQuery.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>User not found</AlertDescription>
        </Alert>
      )}

      {userQuery.data && (
        <Card>
          <CardContent>
            <UserForm
              mode="update"
              initialValues={userQuery.data}
              submitText="Update User"
              isPending={updateUserMutation.isPending}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/users')}
            />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default UserEdit;
